import React, { useLayoutEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  Paper,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { debounce, isEmpty } from "lodash";
import { makeStyles } from "tss-react/mui";

import client from "@/client";
import { graphql } from "@/gql";
import { AllergiesKind, FoodItemFragment, FoodSource } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import SearchIcon from "@components/Icons/SearchIcon";
import Padding from "@components/layout/Padding";

import SearchIngredienItem from "./FoodSelectItem";

interface Props {
  onSelect: (food: FoodItemFragment) => void;
  clientAllergies: AllergiesKind[];
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    borderRadius: 0,
    padding: theme.spacing(4, 3),
  },
  title: {
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 700,
    color: colors.dark,
  },
  closeButton: {
    padding: theme.spacing(1),
    color: colors.grey,
    backgroundColor: "#E8EEF6",
  },
  checkboxLabel: {
    color: colors.grey,
  },
  addButton: {
    padding: theme.spacing(2),
    color: colors.grey,
    backgroundColor: "#E8EEF6",
  },
  helperText: {
    color: colors.grey,
    textAlign: "center",
  },
  list: {
    maxHeight: "30vh",
    overflow: "auto",
  },
  dropdown: {
    flex: 1,
  },
  menu: {
    borderRadius: 4,
    boxShadow: `0 0 0 1px hsla(218, 50%, 10%, 0.1), 0 4px 11px hsla(218, 50%, 10%, 0.1)`,
    marginTop: theme.spacing(0.5),
    position: "absolute",
    zIndex: 2,
    width: "calc(100% - 76px)",
  },
  ingredient: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.grey[100],
      "& input::-webkit-clear-button ": {
        display: "none",
      },
      "& input::-webkit-outer-spin-button": {
        display: "none",
      },
      "& input::-webkit-inner-spin-button": {
        display: "none",
      },
    },
  },
}));

const foodSelectContentQuery = graphql(`
  query FoodSelectContent(
    $sources: [FoodSource!]!
    $name: String!
    $first: Int
    $after: String
  ) {
    foods(name: $name, sources: $sources, first: $first, after: $after) {
      nodes {
        ...FoodItem
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const FoodSelectContent: React.FC<Props> = ({
  onSelect = () => {},
  clientAllergies = [],
}) => {
  const { classes, cx } = useStyles();
  const [searchString, setSearchString] = useState("");
  const setSearch = useMemo(() => debounce(setSearchString, 500), []);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<FoodItemFragment[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const selectedSources = {
    FOOD_DATABASE: false,
    CUSTOM: false,
    CNC_GC: false,
    MCCANCE_AND_WIDOWSONS: false,
    USDA: true,
    FNRI: true,
  };

  const searchFoods = (page: number, loadMore: boolean) => {
    setLoading(true);
    setHasNextPage(false);
    client
      .query({
        query: foodSelectContentQuery,
        variables: {
          name: searchString,
          sources: Object.entries(selectedSources)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([_, checked]) => checked)
            .map(([key]) => key) as FoodSource[],
          first: 8,
        },
      })
      .then((result) => {
        if (loadMore) setResults(results.concat(result.data.foods.nodes));
        else setResults(result.data.foods.nodes);
        setHasNextPage(Boolean(result.data.foods.pageInfo.hasNextPage));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useLayoutEffect(() => {
    setCurrentPage(0);
    searchFoods(0, false);
  }, [searchString]);

  const searchMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    searchFoods(nextPage, true);
  };

  const noResults = !loading && searchString !== "" && isEmpty(results);

  return (
    <Paper className={cx(classes.root)} elevation={4}>
      <Box display="flex" flexDirection="column" margin={6}>
        <TextField
          className={classes.searchField}
          placeholder="Search..."
          size="small"
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="inherit" />
              </InputAdornment>
            ),
          }}
        />
        <List className={classes.list}>
          {noResults && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <Typography className={classes.helperText}>
                No results were found for &quot;{searchString}&quot;
              </Typography>
            </Box>
          )}
          {results?.map((food) => (
            <SearchIngredienItem
              key={food.id}
              food={food}
              onSelect={onSelect}
              clientAllergies={clientAllergies}
            />
          ))}
          {loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <CircularProgress variant="indeterminate" size={24} />
            </Box>
          )}
          {hasNextPage && (
            <Padding vertical={2}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={searchMore}
                  disabled={!hasNextPage}
                >
                  Load more
                </Button>
              </Box>
            </Padding>
          )}
        </List>
      </Box>
    </Paper>
  );
};

export default FoodSelectContent;
